import { useState, useMemo } from 'react';
import { useAuth } from '@/App';
import { Menu, X } from 'lucide-react';
import {
  Building2, Home, CalendarDays, Users, BarChart3, Settings, LogOut,
  MessageCircle, FileText, Heart, Tag, CreditCard, ShieldCheck, Bell, Mail,
  Paintbrush, Sparkles, BookOpen, Download, Send, ClipboardList, FileUp, QrCode
} from 'lucide-react';
import { SidebarHeader } from '@/components/sidebar/SidebarHeader';
import { SidebarNav } from '@/components/sidebar/SidebarNav';
import { SidebarFooter } from '@/components/sidebar/SidebarFooter';

const ADMIN_LINKS = [
  { icon: BarChart3, label: 'Salpicadero', href: '/dashboard', key: 'dashboard', adminOnly: true },
  { icon: Home, label: 'Propiedades', href: '/admin/properties', key: 'admin-properties', adminOnly: true },
  { icon: Download, label: 'Importar Airbnb', href: '/admin/airbnb-import', key: 'airbnb-import', adminOnly: true },
  { icon: ClipboardList, label: 'Solicitudes Reserva', href: '/admin/booking-requests', key: 'booking-requests', adminOnly: true },
  { icon: CalendarDays, label: 'Reservas', href: '/reservations', key: 'reservations', adminOnly: true },
  { icon: CalendarDays, label: 'Calendario', href: '/calendar', key: 'calendar', adminOnly: true },
  { icon: Sparkles, label: 'Eventos', href: '/admin/events', key: 'events', adminOnly: true },
  { icon: BookOpen, label: 'Publicaciónes', href: '/admin/posts', key: 'posts', adminOnly: true },
  { icon: MessageCircle, label: 'Instagram', href: '/admin/instagram', key: 'instagram', adminOnly: true },
  { icon: QrCode, label: 'Landing QR', href: '/admin/qr', key: 'qr', adminOnly: true },
  { icon: Bell, label: 'Notificaciónes', href: '/notifications', key: 'notifications', adminOnly: true },
  { icon: Send, label: 'Newsletter', href: '/admin/newsletter', key: 'newsletter', adminOnly: true },
  { icon: Mail, label: 'Emails', href: '/admin/emails', key: 'emails', adminOnly: true },
  { icon: ShieldCheck, label: 'Verificaciónes', href: '/verification', key: 'verification', adminOnly: true },
  { icon: Heart, label: 'Favoritos', href: '/wishlist', key: 'favorites', adminOnly: false },
  { icon: Tag, label: 'Cupónes', href: '/admin/coupons', key: 'coupons', adminOnly: true },
  { icon: Tag, label: 'Cupónes Temporada', href: '/admin/seasonal-coupons', key: 'seasonal-coupons', adminOnly: true },
  { icon: FileText, label: 'Reportes', href: '/reports', key: 'reports', adminOnly: true },
  { icon: FileUp, label: 'Archivos', href: '/admin/files', key: 'files', adminOnly: true },
  { icon: CreditCard, label: 'Datos de Pago', href: '/admin/payment-data', key: 'payment', adminOnly: true },
  { icon: Paintbrush, label: 'Marca / Diseño', href: '/admin/branding', key: 'branding', adminOnly: true },
  { icon: Settings, label: 'Configuración', href: '/settings', key: 'settings', adminOnly: true },
];

const SUPER_ADMIN_LINKS = [
  { icon: Users, label: 'Usuarios', href: '/users', key: 'users' },
];

export default function AdminSidebar({ activeKey }) {
  const { user, logout, isSuperAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const links = useMemo(() => [
    ...ADMIN_LINKS.filter(l => isSuperAdmin || !l.adminOnly),
    ...(isSuperAdmin ? SUPER_ADMIN_LINKS : [])
  ], [isSuperAdmin]);

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <>
      {/* Mobile toggle */}
      <button
        className="fixed top-4 left-4 z-50 lg:hidden bg-[#264639] rounded-lg p-2 shadow-md"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="sidebar-toggle"
      >
        {sidebarOpen ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
      </button>

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 sidebar-dark transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          <SidebarHeader />
          <SidebarNav links={links} activeKey={activeKey} onLinkClick={closeSidebar} />
          <SidebarFooter user={user} onLogout={logout} />
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={closeSidebar}
        />
      )}
    </>
  );
}
